import React, { useState } from 'react';
import { Language, ServiceExchange } from '@/types';
import { ExchangeCard } from './ExchangeCard';
import { RatingModal } from '../rating/RatingModal';
import { Loading, Alert, Modal, Button } from '@/components/common';
import { getTranslation } from '@/constants';
import { useExchanges } from '@/hooks';
import { useAuth } from '@/context/AuthContext';

interface ScheduledExchangesProps {
  language: Language;
  onChatOpen: (exchangeId: string) => void;
}

export const ScheduledExchanges: React.FC<ScheduledExchangesProps> = ({
  language,
  onChatOpen,
}) => {
  const { user, refreshUserProfile } = useAuth();
  const { scheduledExchanges, cancelService, completeService, loading, error } = useExchanges(user?.uid);
  const t = getTranslation(language);

  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [ratingExchange, setRatingExchange] = useState<ServiceExchange | null>(null);

  const handleCancelClick = (exchangeId: string) => {
    setCancellingId(exchangeId);
  };

  const handleConfirmCancel = async () => {
    if (!cancellingId) return;

    try {
      setCancelling(true);
      await cancelService(cancellingId);
      setCancellingId(null);
    } catch (err) {
      console.error('Error cancelling exchange:', err);
    } finally {
      setCancelling(false);
    }
  };

  const handleCompleteClick = (exchangeId: string) => {
    setCompletingId(exchangeId);
  };

  const handleConfirmComplete = async () => {
    if (!completingId) return;

    // Find the exchange to get the other user's info for rating
    const exchangeToComplete = scheduledExchanges.find((e) => e.id === completingId);

    try {
      setCompleting(true);
      await completeService(completingId);
      // Refresh user profile to update community status
      await refreshUserProfile();
      setCompletingId(null);

      // Open rating modal after successful completion
      if (exchangeToComplete) {
        setRatingExchange(exchangeToComplete);
      }
    } catch (err) {
      console.error('Error completing exchange:', err);
    } finally {
      setCompleting(false);
    }
  };

  // Get the other user's info for rating
  const getOtherUser = (exchange: ServiceExchange) => {
    const isProvider = user?.uid === exchange.providerId;
    return {
      id: isProvider ? exchange.requesterId : exchange.providerId,
      name: isProvider ? exchange.requesterName : exchange.providerName,
    };
  };

  if (loading) {
    return <Loading text={t.loading} />;
  }

  return (
    <>
      <div className="space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        {scheduledExchanges.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t.noExchanges}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {scheduledExchanges.map((exchange) => (
              <ExchangeCard
                key={exchange.id}
                exchange={exchange}
                language={language}
                currentUserId={user?.uid}
                onCancel={handleCancelClick}
                onComplete={handleCompleteClick}
                onChat={onChatOpen}
              />
            ))}
          </div>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={cancellingId !== null}
        onClose={() => !cancelling && setCancellingId(null)}
        title={t.cancel}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            {language === 'en'
              ? 'Are you sure you want to cancel this exchange?'
              : 'Êtes-vous sûr de vouloir annuler cet échange?'}
          </p>

          <div className="flex space-x-3">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setCancellingId(null)}
              disabled={cancelling}
            >
              {language === 'en' ? 'No' : 'Non'}
            </Button>
            <Button
              variant="danger"
              fullWidth
              onClick={handleConfirmCancel}
              loading={cancelling}
            >
              {language === 'en' ? 'Yes, Cancel' : 'Oui, Annuler'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Complete Confirmation Modal */}
      <Modal
        isOpen={completingId !== null}
        onClose={() => !completing && setCompletingId(null)}
        title={language === 'en' ? 'Complete Exchange' : 'Terminer l\'échange'}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            {language === 'en'
              ? 'Mark this exchange as complete? This will update your community status.'
              : 'Marquer cet échange comme terminé? Cela mettra à jour votre statut communautaire.'}
          </p>

          <div className="flex space-x-3">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setCompletingId(null)}
              disabled={completing}
            >
              {language === 'en' ? 'No' : 'Non'}
            </Button>
            <Button
              variant="success"
              fullWidth
              onClick={handleConfirmComplete}
              loading={completing}
            >
              {language === 'en' ? 'Yes, Complete' : 'Oui, Terminer'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Rating Modal - shown after completing an exchange */}
      <RatingModal
        isOpen={ratingExchange !== null}
        onClose={() => setRatingExchange(null)}
        exchangeId={ratingExchange?.id || null}
        toUserId={ratingExchange ? getOtherUser(ratingExchange).id : null}
        toUserName={ratingExchange ? getOtherUser(ratingExchange).name : null}
        language={language}
      />
    </>
  );
};
